/**
 * GrantList — the links a run handed out, and the switch that kills one.
 *
 * A grant IS the access (CONTRACT §1): the emailed URL carries nothing but an opaque
 * token, and the 7-day window is enforced by our `expires_at`, never by an S3 signature.
 * So this list is the only place an operator can answer "can the client still download
 * yesterday's file?" and "make that stop, now".
 *
 * Rules this component exists to keep:
 *   - the token is never stored and never shown. We hold the sha256 hash; it renders as
 *     `…9f3a` and copies in full for correlating with a resolver log line (§8.6).
 *   - revoke is irreversible and instant — the resolver checks `revoked_at` before
 *     expiry — so it goes through ConfirmDialog with the client id typed out.
 *   - re-issue mints a NEW token. It never extends the one that is expiring, and the
 *     copy here says so, because "extend" is what everybody assumes it does.
 *   - every time shown is IST and labelled (§8.7).
 *
 * Usable two ways: hand it `grants` you already have, or hand it a `runId` and let it
 * fetch. Both are supported because the run screen has the grants loaded already and the
 * dashboard does not.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import Button from '@/components/Button';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import Table, { type Column } from '@/components/Table';
import { Badge, LinkBadge } from '@/components/Badge';
import { Field, Input } from '@/components/Field';
import { useToast } from '@/components/Toast';
import { api, errorMessage, http, publicDownloadUrl } from '@/lib/api';
import {
  basename,
  formatBytes,
  formatCount,
  istDateTime,
  istDateTimeExact,
  linkState,
  maskToken,
  truncate,
} from '@/lib/format';
import type { Grant, LinkState } from '@/lib/types';

// ---------------------------------------------------------------------------
// Wire shape
// ---------------------------------------------------------------------------

/**
 * What `public_grant()` actually returns. `lib/types.ts` has the contract's own fields;
 * these are the denormalised extras the API adds for the console, declared here rather
 * than widened in the shared file (which this component does not own).
 */
export interface GrantRow extends Grant {
  token_hint?: string;
  filename?: string;
  label?: string;
  bytes?: number;
  issued_at_ist?: string;
  expires_at_ist?: string;
  status?: string;
  remaining_seconds?: number;
  access_count_truncated?: boolean;
  revoked?: boolean;
}

/** The one and only copy of a token, returned by POST /api/runs/{run_id}/grants. */
export interface MintedGrant extends GrantRow {
  /** Never log this, never render it, never put it in a toast. */
  token: string;
  /** Absolute `/d/{token}` URL, empty when PUBLIC_BASE_URL is unset on the deployment. */
  url?: string;
  expires_in_days?: number;
  warning?: string;
}

/**
 * Mint a fresh console download link for one artifact of a run.
 *
 * This is how the console downloads anything: it does NOT presign the object, it creates
 * a grant exactly like a recipient's, so the same 7-day window, the same revoke switch
 * and the same per-click audit trail apply to an internal pull as to a client delivery.
 */
export async function mintGrant(
  runId: string,
  target: { s3_key?: string; artifact_kind?: string } = {},
): Promise<MintedGrant> {
  return http.post<MintedGrant>(`/api/runs/${encodeURIComponent(runId)}/grants`, target);
}

/** The URL to open for a minted grant; falls back to building it from the token. */
export function mintedUrl(minted: MintedGrant): string {
  return minted.url || publicDownloadUrl(minted.token);
}

// ---------------------------------------------------------------------------
// Clipboard
// ---------------------------------------------------------------------------

export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the textarea path: clipboard permission can be denied outright
    // on a locked-down operator laptop.
  }
  try {
    const area = document.createElement('textarea');
    area.value = value;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Live first, then the ones running out, then the dead. Used by sortBy="expiry". */
const STATE_RANK: Record<LinkState, number> = { live: 0, expiring: 1, expired: 2, revoked: 3 };

export interface GrantListProps {
  /** Grants you already have. Takes precedence over `runId`. */
  grants?: GrantRow[];
  /** Fetch the grants for this run instead of being handed them. */
  runId?: string;
  loading?: boolean;
  /** Render an error state instead of an empty one (an ApiError, or any thrown value). */
  error?: unknown;
  /** Show the run column — for a cross-run list such as the dashboard's expiring links. */
  showRun?: boolean;
  /** Show the client column. Defaults to on when `showRun` is on. */
  showClient?: boolean;
  dense?: boolean;
  /** 'expiry' puts the closest expiry first — what the dashboard wants. */
  sortBy?: 'expiry' | 'issued' | 'none';
  /** Hide the revoke control entirely (a read-only audience). */
  canRevoke?: boolean;
  caption?: string;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  footer?: ReactNode;
  /** Called after a successful revoke, with the updated grant. */
  onRevoked?: (grant: GrantRow) => void;
  /** Called when the internal refresh finishes, for a parent that tracks counts. */
  onLoaded?: (grants: GrantRow[]) => void;
}

export function GrantList({
  grants,
  runId,
  loading = false,
  error,
  showRun = false,
  showClient,
  dense = true,
  sortBy = 'issued',
  canRevoke = true,
  caption,
  emptyTitle = 'No links issued',
  emptyDescription = 'A grant is created for each recipient when a run is dispatched, and for you when you download from this console.',
  emptyAction,
  footer,
  onRevoked,
  onLoaded,
}: GrantListProps) {
  const toast = useToast();
  const controlled = grants !== undefined;

  const [rows, setRows] = useState<GrantRow[]>(grants ?? []);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [target, setTarget] = useState<GrantRow | null>(null);
  const [reason, setReason] = useState('');
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    if (controlled) setRows(grants ?? []);
  }, [controlled, grants]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!runId) return;
      setFetching(true);
      setFetchError(null);
      try {
        const page = await api.runs.grants(runId, { limit: 100 }, signal ? { signal } : {});
        if (signal?.aborted) return;
        const items = page.items as GrantRow[];
        setRows(items);
        onLoadedRef.current?.(items);
      } catch (err) {
        if (signal?.aborted) return;
        setFetchError(err);
      } finally {
        if (!signal?.aborted) setFetching(false);
      }
    },
    [runId],
  );

  useEffect(() => {
    if (controlled || !runId) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [controlled, runId, load]);

  const busy = loading || (!controlled && fetching);
  const shownError = error ?? fetchError;

  const sorted = [...rows];
  if (sortBy === 'expiry') {
    // Ascending expiry alone put the DEAD links first and sorted the grant the operator
    // had just minted to the bottom. The question this list answers is "can anyone still
    // get this file", so the rows where the answer is yes lead, soonest to expire first.
    sorted.sort((a, b) => {
      const rank = STATE_RANK[linkState(a).state] - STATE_RANK[linkState(b).state];
      if (rank !== 0) return rank;
      return (a.expires_at ?? 0) - (b.expires_at ?? 0);
    });
  } else if (sortBy === 'issued') {
    sorted.sort((a, b) => (b.issued_at ?? 0) - (a.issued_at ?? 0));
  }

  const withClient = showClient ?? showRun;

  const handleCopy = async (grant: GrantRow) => {
    const ok = await copyText(grant.token_hash);
    if (ok) {
      toast.success('Token hash copied', {
        description: 'The hash, not the token — the token itself is never stored.',
      });
    } else {
      toast.warn('Could not reach the clipboard', {
        description: `Hash ends ${maskToken(grant.token_hash)}.`,
      });
    }
  };

  const confirmRevoke = async () => {
    if (!target) return;
    let updated: Grant;
    try {
      updated = await api.grants.revoke(target.token_hash, reason ? { reason } : {});
    } catch (err) {
      // ConfirmDialog deliberately stays open on a rejection so the operator can retry;
      // it does not report the reason, so we have to.
      toast.error(errorMessage(err));
      throw err;
    }
    const merged: GrantRow = { ...target, ...(updated as GrantRow) };
    setRows((current) =>
      current.map((g) => (g.token_hash === merged.token_hash ? merged : g)),
    );
    onRevoked?.(merged);
    toast.success('Link revoked', {
      description: 'The next click returns 410 Gone. Re-issuing mints a new token.',
    });
    setTarget(null);
    setReason('');
  };

  const columns: Column<GrantRow>[] = [
    {
      key: 'state',
      header: 'Link',
      width: '104px',
      nowrap: true,
      headerTitle: 'State of OUR grant window (contract §1) — never an S3 signature',
      // The state WORD only. The countdown is the second line of Expires (IST); printing
      // it here as well spent two columns of a seven-column table on one fact.
      render: (g) => <LinkBadge grant={g} showCountdown={false} />,
    },
    {
      key: 'kind',
      header: 'Kind',
      width: '84px',
      nowrap: true,
      // A classification, not a state: it cannot be wrong and never changes on its own,
      // so it is a mark rather than a pill competing with the link state beside it.
      render: (g) =>
        g.kind === 'recording' ? (
          <Badge
            variant="plain"
            glyph="♪"
            title="Recording playback — re-signed for 60s at click time"
          >
            recording
          </Badge>
        ) : (
          <Badge variant="plain" glyph="▤" title="Report download">
            report
          </Badge>
        ),
    },
    {
      key: 'recipient',
      // No width: this column and File absorb the table's slack. They are what
      // identifies the grant, and they were the two being squeezed off the edge.
      header: 'Issued to',
      render: (g) => {
        const isConsole = String(g.recipient_id ?? '').startsWith('console:');
        return (
          // The address in FULL. maskEmail's own docstring says it is for logs; masking
          // it here made "did this go to the wrong desk?" unanswerable, because the mask
          // keeps the domain (identical across a client) and destroys the local part
          // (the only thing that differs).
          <span className="cluster-tight" style={{ minWidth: 0 }}>
            {isConsole ? (
              <Badge variant="plain" glyph="⌂" title="Minted by an operator in this console">
                console
              </Badge>
            ) : null}
            {/* nowrap and no overflow-wrap: an address is never broken mid-local-part.
                This column is one of the two width-less ones, so it has the room. */}
            <span className="mono small nowrap">
              {g.recipient_email || (isConsole ? 'this console' : '—')}
            </span>
          </span>
        );
      },
    },
    {
      key: 'file',
      // Also width-less: the column width, not a magic character count, decides what
      // shows. `.truncate` clips with an ellipsis and keeps the full key on the title.
      header: 'File',
      render: (g) => (
        <div className="stack-tight">
          <span className="mono small truncate" title={g.s3_key}>
            {g.filename || basename(g.s3_key)}
          </span>
          <span className="xs dim">
            {g.label ? `${g.label} · ` : ''}
            {g.bytes ? formatBytes(g.bytes) : 'size not recorded'}
          </span>
        </div>
      ),
    },
    {
      key: 'expires',
      header: 'Expires (IST)',
      width: '176px',
      nowrap: true,
      headerTitle: 'Our expires_at. Files are not deleted when a link expires.',
      render: (g) => (
        <div className="stack-tight">
          <span className="small" title={`Issued ${istDateTime(g.issued_at)}`}>
            {istDateTime(g.expires_at)}
          </span>
          <span className="xs dim">{linkState(g).label}</span>
        </div>
      ),
    },
    {
      key: 'issued',
      header: 'Issued (IST)',
      width: '160px',
      nowrap: true,
      // Rank 4 — the first and only column allowed to go. The expiry cell carries the
      // issued time on its title, so nothing is lost when it does.
      optional: true,
      render: (g) => <span className="small">{istDateTime(g.issued_at)}</span>,
    },
    {
      key: 'access',
      header: 'Clicks',
      width: '76px',
      align: 'right',
      mono: true,
      headerTitle: 'Downloads recorded in nrd-access for this grant',
      // "we do not know" and "nobody clicked it" are opposite conclusions on a revoke
      // decision, so they no longer render as the same em dash with the same tooltip.
      render: (g) => {
        const counted = g.access_count !== null && g.access_count !== undefined;
        if (!counted) {
          return (
            <span className="dim" title="No click count recorded for this grant">
              not counted
            </span>
          );
        }
        return (
          <span
            title={
              g.last_accessed_at
                ? `Last ${istDateTimeExact(g.last_accessed_at)}`
                : g.access_count === 0
                  ? 'Never opened'
                  : 'No last-access time recorded'
            }
          >
            {`${formatCount(g.access_count)}${g.access_count_truncated ? '+' : ''}`}
          </span>
        );
      },
    },
    {
      key: 'token',
      header: 'Token',
      width: '92px',
      nowrap: true,
      headerTitle: 'sha256 of the token. The token itself is never stored.',
      render: (g) => (
        <button
          type="button"
          className="btn btn-ghost btn-sm mono"
          onClick={() => void handleCopy(g)}
          title="Copy the full token hash"
        >
          {maskToken(g.token_hash)}
        </button>
      ),
    },
  ];

  if (withClient) {
    columns.splice(2, 0, {
      key: 'client',
      header: 'Client',
      /* The one width-less column, so there is something to absorb the slack under
         `table-layout: fixed`. With eleven columns all declaring a width this table
         overflowed sideways in any pane narrower than the sum of them, hiding the
         right-hand columns — which is the fault the fixed layout was introduced to fix. */
      render: (g) => (
        <Link className="truncate" to={`/clients/${encodeURIComponent(g.client_id)}`}>
          {g.client_id}
        </Link>
      ),
    });
  }

  if (showRun) {
    columns.splice(withClient ? 3 : 2, 0, {
      key: 'run',
      header: 'Run',
      width: '132px',
      nowrap: true,
      mono: true,
      render: (g) => (
        <Link to={`/runs/${encodeURIComponent(g.run_id)}`} title={g.run_id}>
          {truncate(g.run_id, 14, '—')}
        </Link>
      ),
    });
  }

  if (canRevoke) {
    columns.push({
      key: 'actions',
      header: '',
      width: '88px',
      nowrap: true,
      align: 'right',
      render: (g) => {
        const state = linkState(g).state;
        if (state === 'revoked') {
          return (
            <span className="xs dim" title={linkState(g).label}>
              revoked
            </span>
          );
        }
        return (
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              setReason('');
              setTarget(g);
            }}
            disabled={state === 'expired'}
            title={
              state === 'expired'
                ? 'Already past its window — the resolver returns 410 for this token.'
                : 'Kill this link now'
            }
          >
            Revoke
          </Button>
        );
      },
    });
  }

  const targetState = target ? linkState(target) : null;

  return (
    <>
      <Table<GrantRow>
        columns={columns}
        rows={sorted}
        rowKey={(g) => g.token_hash}
        loading={busy}
        dense={dense}
        caption={caption}
        skeletonRows={3}
        rowTone={(g) => {
          const state = linkState(g).state;
          if (state === 'revoked') return 'muted';
          if (state === 'expiring') return 'warn';
          if (state === 'expired') return 'muted';
          return 'default';
        }}
        footer={footer}
        empty={
          shownError ? (
            <EmptyState
              tone="danger"
              title="Could not load links"
              description={errorMessage(shownError)}
              action={
                runId && !controlled ? (
                  <Button variant="primary" onClick={() => void load()}>
                    Retry
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
          )
        }
      />

      <ConfirmDialog
        open={Boolean(target)}
        tone="danger"
        title="Revoke this download link?"
        confirmLabel="Revoke link"
        confirmPhrase={target?.client_id}
        consequence={
          target
            ? `The next click returns 410 Gone. ${
                String(target.recipient_id ?? '').startsWith('console:')
                  ? 'This console link'
                  : target.recipient_email
              } loses access to ${target.filename || basename(target.s3_key)} immediately, and re-issuing mints a new token rather than bringing this one back.`
            : undefined
        }
        onConfirm={confirmRevoke}
        onCancel={() => {
          setTarget(null);
          setReason('');
        }}
      >
        {target ? (
          <div className="stack">
            <dl className="kv">
              <dt>Client</dt>
              <dd className="mono">{target.client_id}</dd>
              <dt>Run</dt>
              <dd className="mono small">{target.run_id}</dd>
              <dt>File</dt>
              <dd className="mono small">{target.filename || basename(target.s3_key)}</dd>
              <dt>Token</dt>
              <dd className="mono">{maskToken(target.token_hash)}</dd>
              <dt>Window</dt>
              <dd>
                {istDateTime(target.issued_at)} → {istDateTime(target.expires_at)}
                {targetState ? <span className="dim"> · {targetState.label}</span> : null}
              </dd>
              <dt>Clicks so far</dt>
              <dd className="mono">
                {target.access_count === null || target.access_count === undefined
                  ? 'not counted'
                  : formatCount(target.access_count)}
              </dd>
            </dl>
            <Field
              label="Reason"
              optional
              hint="Recorded on the audit row. Whoever reads this in three months is not in the room now."
            >
              {(p) => (
                <Input
                  {...p}
                  value={reason}
                  maxLength={512}
                  placeholder="Sent to the wrong distribution list"
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>
            <p className="small muted">
              The file itself is untouched — expiring or revoking a link never deletes a
              client&apos;s data (contract §1).
            </p>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

export default GrantList;
